import React, { useState, useEffect, useRef } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { navigation } from '../data/features';

const homeRoutes = ['/', '/features', '/platform', '/solutions', '/technology', '/subscriptions', '/contact'];

const sectionIds = navigation.map((item) => item.href);

export default function Header({ onGetStarted }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState('home');
  const location = useLocation();
  const ticking = useRef(false);

  const isHomeFlow = homeRoutes.includes(location.pathname);

  useEffect(() => {
    const map = {
      '/': 'home',
      '/features': 'features',
      '/platform': 'platform',
      '/solutions': 'ai',
      '/technology': 'why',
      '/subscriptions': 'subscriptions',
      '/contact': 'contact',
    };
    if (isHomeFlow) setActive(map[location.pathname] || 'home');
  }, [location.pathname, isHomeFlow]);

  useEffect(() => {
    const onScrollChrome = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScrollChrome, { passive: true });
    return () => window.removeEventListener('scroll', onScrollChrome);
  }, []);

  /* Scroll-spy: highlight the nav tab for the section currently in view */
  useEffect(() => {
    if (!isHomeFlow) return undefined;

    const headerOffset = 120;

    const updateActiveFromScroll = () => {
      const scrollBottom = window.scrollY + window.innerHeight;
      const docHeight = document.documentElement.scrollHeight;

      // Near page bottom → Contact
      if (docHeight - scrollBottom < 80) {
        setActive('contact');
        return;
      }

      let current = 'home';
      for (const id of sectionIds) {
        const el = document.getElementById(id);
        if (!el) continue;
        if (el.getBoundingClientRect().top - headerOffset <= 0) {
          current = id;
        }
      }
      setActive((prev) => (prev === current ? prev : current));
    };

    const onScroll = () => {
      if (ticking.current) return;
      ticking.current = true;
      requestAnimationFrame(() => {
        updateActiveFromScroll();
        ticking.current = false;
      });
    };

    // Wait for route-driven smooth scroll to settle, then spy continuously
    const start = setTimeout(() => {
      updateActiveFromScroll();
      window.addEventListener('scroll', onScroll, { passive: true });
    }, 350);

    return () => {
      clearTimeout(start);
      window.removeEventListener('scroll', onScroll);
    };
  }, [location.pathname, isHomeFlow]);

  useEffect(() => setMobileOpen(false), [location.pathname]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled ? 'border-b border-white/10 bg-black/80 backdrop-blur-xl' : 'bg-transparent'
      }`}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:h-[4.25rem] sm:px-8 lg:px-12">
        <RouterLink
          to="/"
          className="flex items-center gap-2.5"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          <img src="/Zuntly_New.png" alt="Zuntly" className="h-8 w-8 rounded-lg object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          <span className="text-lg font-bold tracking-tight text-white">Zuntly</span>
        </RouterLink>

        <nav className="hidden items-center gap-1 md:flex">
          {navigation.map((item) => (
            <RouterLink
              key={item.name}
              to={item.path}
              onClick={() => setActive(item.href)}
              className={`rounded-lg px-3 py-2 text-sm transition-colors ${
                active === item.href ? 'bg-white/10 text-white' : 'text-mute hover:text-white'
              }`}
            >
              {item.name}
            </RouterLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button type="button" onClick={onGetStarted} className="btn-ghost-sm hidden sm:inline-flex">
            Get in touch
          </button>
          <button
            type="button"
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/20 text-white md:hidden"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Menu"
          >
            {mobileOpen ? '×' : '☰'}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="border-t border-white/10 bg-black/95 px-5 py-4 md:hidden">
          <nav className="flex flex-col gap-1">
            {navigation.map((item) => (
              <RouterLink
                key={item.name}
                to={item.path}
                onClick={() => setActive(item.href)}
                className={`rounded-lg px-3 py-3 text-sm ${
                  active === item.href ? 'bg-white/10 text-white' : 'text-mute hover:text-white'
                }`}
              >
                {item.name}
              </RouterLink>
            ))}
            <button type="button" onClick={onGetStarted} className="btn-grad mt-2">
              Get in touch
            </button>
          </nav>
        </div>
      )}
    </header>
  );
}
